"use client";

import { useEffect, useId, useMemo, useState } from "react";
import { Book, Layers, Newspaper, Search, Square, X } from "lucide-react";

import type { BookSummary } from "@/lib/lumi-api";
import AddBookDialog from "./AddBookDialog";
import RustBookCard from "./RustBookCard";
import {
  SORT_OPTIONS,
  countByKind,
  libraryView,
  readStoredCoverStyle,
  writeStoredCoverStyle,
  type CoverStyle,
  type KindFilter,
  type SortKey,
} from "./libraryView";

interface LibraryBrowserProps {
  books: BookSummary[];
}

/**
 * Search / filter / sort chrome for the library grid.
 *
 * The page itself stays a server component (auth + fetch); this owns the
 * browsing state. All filtering is client-side over the already-fetched list —
 * the endpoint caps at 100 items, so there is nothing to gain from round-trips
 * per keystroke, and it keeps search instant.
 */
export default function LibraryBrowser({ books }: LibraryBrowserProps) {
  const searchId = useId();
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [sort, setSort] = useState<SortKey>("added");
  // Server render is always the default so markup matches; the stored setting
  // is applied on mount.
  const [coverStyle, setCoverStyle] = useState<CoverStyle>("flat");

  useEffect(() => {
    setCoverStyle(readStoredCoverStyle());
  }, []);

  function chooseCoverStyle(next: CoverStyle) {
    setCoverStyle(next);
    writeStoredCoverStyle(next);
  }

  const counts = useMemo(() => countByKind(books), [books]);
  // Only worth showing the Books/Articles split when the library actually
  // holds both — otherwise it's a control that can only ever empty the grid.
  const showKindFilter = counts.book > 0 && counts.article > 0;

  const visible = useMemo(
    () => libraryView(books, { query, kind: showKindFilter ? kind : "all", sort }),
    [books, query, kind, sort, showKindFilter],
  );

  const kindTabs: Array<{ value: KindFilter; label: string; count: number }> = [
    { value: "all", label: "All", count: books.length },
    { value: "book", label: "Books", count: counts.book },
    { value: "article", label: "Articles", count: counts.article },
  ];

  return (
    <>
      <div className="mb-6 mt-8 flex flex-col gap-4">
        {/* Row 1 — search + add. */}
        <div className="flex items-center gap-3">
          <div className="relative min-w-0 flex-1 sm:max-w-md">
            <label className="sr-only" htmlFor={searchId}>
              Search your library
            </label>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-body"
            />
            {/* text-base (16px) on purpose: anything smaller makes mobile
                Safari zoom the page on focus. */}
            <input
              autoComplete="off"
              className="h-11 w-full rounded-md border border-app-muted bg-app-surface pl-9 pr-10 text-base text-app-text placeholder:text-app-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal focus-visible:ring-offset-2 focus-visible:ring-offset-app-bg"
              id={searchId}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by title or language"
              type="search"
              value={query}
            />
            {query && (
              <button
                aria-label="Clear search"
                className="absolute right-1 top-1/2 flex h-11 w-9 -translate-y-1/2 items-center justify-center rounded-md text-app-body hover:text-app-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal"
                onClick={() => setQuery("")}
                type="button"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="shrink-0 sm:ml-auto">
            <AddBookDialog />
          </div>
        </div>

        {/* Row 2 — organise: kind, sort, cover style. */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {showKindFilter && (
            <div
              aria-label="Filter by type"
              className="flex flex-wrap items-center gap-1 rounded-md border border-app-border bg-app-tinted p-1"
              role="group"
            >
              {kindTabs.map((tab) => {
                const active = kind === tab.value;
                const Icon =
                  tab.value === "book"
                    ? Book
                    : tab.value === "article"
                      ? Newspaper
                      : Layers;
                return (
                  <button
                    aria-pressed={active}
                    className={`inline-flex h-9 items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal ${
                      active
                        ? "bg-app-accent text-app-on-accent"
                        : "text-app-body hover:bg-app-surface"
                    }`}
                    key={tab.value}
                    onClick={() => setKind(tab.value)}
                    type="button"
                  >
                    <Icon aria-hidden="true" className="h-4 w-4" />
                    {tab.label}
                    <span className="tabular-nums opacity-80">{tab.count}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 md:ml-auto">
            <div className="flex items-center gap-2">
              <label
                className="text-sm text-app-body"
                htmlFor={`${searchId}-sort`}
              >
                Sort
              </label>
              <select
                className="h-11 rounded-md border border-app-muted bg-app-surface px-2 text-base text-app-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal"
                id={`${searchId}-sort`}
                onChange={(event) => setSort(event.target.value as SortKey)}
                value={sort}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* The 3D presentation setting. Lives here rather than in Account
                because it changes what you are looking at while you look at it. */}
            <div
              aria-label="Cover style"
              className="flex items-center gap-1 rounded-md border border-app-border bg-app-tinted p-1"
              role="group"
            >
              {(
                [
                  { value: "flat", label: "Flat", Icon: Square },
                  { value: "solid", label: "3D", Icon: Layers },
                ] as const
              ).map(({ value, label, Icon }) => {
                const active = coverStyle === value;
                return (
                  <button
                    aria-pressed={active}
                    className={`inline-flex h-9 items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal ${
                      active
                        ? "bg-app-accent text-app-on-accent"
                        : "text-app-body hover:bg-app-surface"
                    }`}
                    key={value}
                    onClick={() => chooseCoverStyle(value)}
                    title={
                      value === "flat"
                        ? "Flat covers"
                        : "3D covers — show spine and thickness"
                    }
                    type="button"
                  >
                    <Icon aria-hidden="true" className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Result count doubles as the live region for search feedback. */}
      <p aria-live="polite" className="mb-4 text-sm text-app-body">
        {visible.length === books.length
          ? `${books.length} item${books.length === 1 ? "" : "s"}`
          : `${visible.length} of ${books.length} items`}
      </p>

      {visible.length === 0 ? (
        <div className="mb-16 rounded-md border border-app-border bg-app-tinted p-8 text-center">
          <p className="text-app-text">
            Nothing here matches{" "}
            {query ? (
              <>
                &ldquo;<span className="font-semibold">{query}</span>&rdquo;
              </>
            ) : (
              "this filter"
            )}
            .
          </p>
          <button
            className="mt-3 min-h-[44px] rounded-md border border-app-muted px-4 text-sm font-medium text-app-text hover:bg-app-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-teal"
            onClick={() => {
              setQuery("");
              setKind("all");
            }}
            type="button"
          >
            Clear search and filters
          </button>
        </div>
      ) : (
        <div className="mb-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map((book) => (
            <RustBookCard book={book} coverStyle={coverStyle} key={book.id} />
          ))}
        </div>
      )}
    </>
  );
}
